/**
 * Auth context — hydrates the current user from /auth/me on boot,
 * exposes `signIn`, `signOut`, `can(resource, action)` to the tree.
 *
 * Beforehand, register `useAuthInit()` once at the App component so
 * the router context can subscribe to login state.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { api, ApiError, type Me } from "./api";

type AuthState = {
  user: Me | null;
  loading: boolean;
};

type AuthApi = AuthState & {
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  can: (resource: string, action: string) => boolean;
  hasRole: (...slugs: string[]) => boolean;
  refresh: () => Promise<void>;
};

const AuthCtx = createContext<AuthApi | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ user: null, loading: true });

  const refresh = useCallback(async () => {
    try {
      const me = await api.get<Me>("/auth/me");
      setState({ user: me, loading: false });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setState({ user: null, loading: false });
      } else {
        // Network-error: keep loading=false so the UI can render the login page.
        setState({ user: null, loading: false });
      }
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  const signIn = useCallback(async (email: string, password: string) => {
    const res = await api.post<{ user: Me }>("/auth/login", { email, password });
    setState({ user: res.user, loading: false });
  }, []);

  const signOut = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    setState({ user: null, loading: false });
  }, []);

  const can = useCallback(
    (resource: string, action: string) => state.user?.permissions.includes(`${resource}:${action}`) ?? false,
    [state.user],
  );

  const hasRole = useCallback(
    (...slugs: string[]) => Boolean(state.user?.roles.some((r) => slugs.includes(r))),
    [state.user],
  );

  const value = useMemo(
    () => ({ ...state, signIn, signOut, can, hasRole, refresh }),
    [state, signIn, signOut, can, hasRole, refresh],
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth(): AuthApi {
  const v = useContext(AuthCtx);
  if (!v) throw new Error("useAuth must be used within <AuthProvider>");
  return v;
}

/** Used by router context — returns the live AuthApi reference. */
export function useAuthInit(): AuthApi {
  return useAuth();
}
